import { Component } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to the browser console for local debugging.
    console.error("[ErrorBoundary] uncaught error:", error, info?.componentStack);

    // Fire-and-forget crash report to the backend so silent prod errors
    // show up in the admin audit log. Never let this throw.
    try {
      api.post("/errors", {
        message: error?.message || String(error),
        stack: error?.stack || null,
        component_stack: info?.componentStack || null,
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      }).catch(() => {});
    } catch (_) {
      /* swallow */
    }
  }

  reset = () => this.setState({ error: null });

  reload = () => window.location.reload();

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen grid place-items-center bg-[#0b0d12] text-slate-200 p-6"
      >
        <div className="max-w-md w-full card-surface p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/40 grid place-items-center mx-auto mb-4 text-[color:var(--accent)]">
            <AlertTriangle size={22} />
          </div>
          <h1 className="text-xl font-black mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-400 mb-1">
            That last action hit a snag. Nothing was lost — reload to keep going.
          </p>
          <pre
            data-testid="error-boundary-message"
            className="text-[11px] text-slate-500 bg-[#0b0d12] border border-slate-800 rounded-lg p-2 mt-3 mb-5 text-left overflow-auto max-h-32 whitespace-pre-wrap"
          >
            {msg}
          </pre>
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.reset}
              data-testid="error-boundary-dismiss"
              className="px-4 py-2 rounded-lg border border-slate-700 hover:border-[color:var(--accent)] text-sm text-slate-200 transition-colors"
            >
              Dismiss
            </button>
            <button
              onClick={this.reload}
              data-testid="error-boundary-reload"
              className="px-4 py-2 rounded-lg bg-[color:var(--accent)] text-white font-bold text-sm inline-flex items-center gap-2 hover:bg-[color:var(--accent-hover)] transition-colors"
            >
              <RotateCcw size={14} /> Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
